
import { AsyncPipe, NgClass } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    inject
} from "@angular/core";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { HeaderComponent } from "@components/header/header.component";
import { IconStackedComponent } from "@components/icon-stacked/icon-stacked.component";
import { InlineToastComponent } from "@components/inline-toast/inline-toast.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import {
    Observable,
    Subject,
    combineLatest,
    forkJoin,
    of
} from "rxjs";
import { catchError, map, switchMap, takeUntil } from "rxjs/operators";

import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { PermittedDirective } from "~app/common/directives/permitted/permitted.directive";
import { EntityType } from "~app/common/models/constants";
import { EntityDTO } from "~app/common/models/entity-models";
import { IOption } from "~app/common/models/options-models";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { canEdit } from "~app/role-permissions";
import { AtgardEntity } from "../../models/atgard-models";
import { INvbAggregatedStatus, INvbUtskriftItem, getNvbUtskriftVariant } from "../../models/naturvardesbedomning-models";
import { AvtalService } from "../../services/avtal.service";
import { NavigationService } from "../../services/navigation.service";
import { VirkeskopValidationService } from "../../services/validation/virkeskop-validation.service";
import { AtgardItemComponent } from "../atgard-item/atgard-item.component";
import { NvbUtskriftDialogComponent } from "../atgard-nvb/forms/nvb-utskrift-dialog/nvb-utskrift-dialog.component";
import { VardeberakningComponent } from "../vardeberakning/vardeberakning.component";

@Component({
    selector: "rask-atgard-list",
    templateUrl: "./atgard-list.component.html",
    styleUrls: ["./atgard-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TogglePanelComponent,
        ActionButtonComponent,
        InlineToastComponent,
        AtgardItemComponent,
        VardeberakningComponent,
        IconStackedComponent,
        HeaderComponent,
        NgClass,
        AsyncPipe,
        PermittedDirective
    ]
})
export class AtgardListComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild(TogglePanelComponent) togglePanel!: TogglePanelComponent;

    atgarder$: Observable<EntityDTO[]>;
    isReadonly$: Observable<boolean>;
    showRecentlyAddedAtgard = false;
    timerId = undefined;
    protected kontraktGuid: string;
    protected kontraktstyp: string;
    protected affarsform: string;
    protected kontraktid: number;
    protected atgardAdded$ = new Subject<void>();
    protected atgardsTyper$: Observable<IOption[]>;
    protected nvbSummary$: Observable<INvbAggregatedStatus>;

    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly avtalService = inject(AvtalService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly navigationService = inject(NavigationService);
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly modalService = inject(NgbModal);
    private readonly canEdit = canEdit();

    constructor() {
        this.isReadonly$ = this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit())
        );
    }

    ngAfterViewInit(): void {
        combineLatest([
            this.atgarder$,
            this.navigationService.atgardId$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([
                entityLista,
                atgardId
            ]) => {
                if (atgardId) {
                    this.togglePanel.forceExpandedState(
                        !!entityLista.find(x => x.id === atgardId)
                    );
                }
            });
    }
    ngOnInit(): void {
        const atgarderPrel$ = this.kontraktMediatorService.atgarderPreload$;
        this.atgarder$ = atgarderPrel$.pipe(
            map(atg => atg.filter(x => x.type !== EntityType.virkeskopsAtgardSkogsvard))
        );

        this.nvbSummary$ = this.atgarder$.pipe(
            switchMap(atgarder => this.getNvbSummary(atgarder))
        );

        this.kontraktMediatorService.kontrakt$.pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(kontrakt => {
            this.kontraktid = kontrakt.id;
            this.affarsform = kontrakt.affarsform;
            this.kontraktstyp = kontrakt.kontraktTyp;
            this.kontraktGuid = kontrakt.guid;
        });

        this.atgardsTyper$ = this.kontraktMediatorService.atgardsTyper$.pipe(
            map(r => r.atgardstyper.map(t => ({
                key: t.kod,
                value: t.namn,
                grupp: t.grupp
            })))
        );
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    handleNvbIconClick(): void {
        const atgarder = this.kontraktMediatorService.atgarderPreload;
        const results$ = atgarder.length === 0
            ? of([])
            : forkJoin(
                atgarder.filter(x => ObjectUtils.hasValue(x.id)).map(atgard => this.avtalService.getNaturvardesBedomning(atgard.id).pipe(
                    map(naturvardesbedomning => ({
                        atgard,
                        naturvardesbedomning
                    })),
                    takeUntil(this.ngUnsubscribe$),
                    catchError(() => of(null))
                ))
            );

        results$.subscribe(results => {
            const items: INvbUtskriftItem[] = results
                .filter(result => !ObjectUtils.isNullOrUndefined(result))
                .map(result => ({
                    title: this.getAtgardUtskriftTitel(result.atgard),
                    naturvardesbedomning: result.naturvardesbedomning,
                    variant: getNvbUtskriftVariant(result.naturvardesbedomning)
                }))
                .filter(item => !ObjectUtils.isNullOrUndefined(item.variant));

            const options = {
                size: "xl",
                backdrop: "static",
                windowClass: "nvb-utskrift-modal-window"
            } as NgbModalOptions;
            const modalRef = this.modalService.open(NvbUtskriftDialogComponent, options);
            (modalRef.componentInstance as NvbUtskriftDialogComponent).items = items;
        });
    }
    addAtgard(typ: IOption) {
        this.atgardAdded$.next();

        const atgardGuid = GuidUtils.newGuid();
        const atgard: AtgardEntity = {
            id: null,
            name: typ.key,
            type: EntityType.virkeskopsAtgard,
            guid: atgardGuid,
            avverkningsTyp: {
                kod: typ.key,
                grupp: typ.grupp
            }
        };
        this.kontraktMediatorService.addAtgard(atgard);
    }

    protected getNvbTooltip(summary: INvbAggregatedStatus): string {
        if (!summary || summary.total === 0) {
            return "Inga åtgärder att bedöma";
        }
        if (summary.missing === 0) {
            return `NV-bedömning utförd på samtliga ${summary.total} åtgärder`;
        }
        return `${summary.missing} av ${summary.total} åtgärder saknar NV-bedömning`;
    }

    protected trackByFn(_index: number, item: EntityDTO) {
        return item.guid;
    }

    protected getIcon(): string {
        return this.validationService.getEntitySeverity("atgard", this.kontraktGuid);
    }

    private getNvbSummary(atgarder: EntityDTO[]): Observable<INvbAggregatedStatus> {
        if (!atgarder || atgarder.length === 0) {
            return of({ total: 0,
                missing: 0 });
        }

        return forkJoin(
            atgarder.filter(x => ObjectUtils.hasValue(x.id)).map(a => this.avtalService.getAtgardStatuses(a.id).pipe(
                catchError(() => of(null))
            ))
        ).pipe(
            map(statuses => {
                const relevanta = statuses.filter(s => s?.naturvardesbedomning?.nvbKravsEj !== true);
                const missing = relevanta.filter(s => ObjectUtils.isNullOrUndefined(s?.naturvardesbedomning?.utfordDatum)).length;
                return { total: relevanta.length,
                    missing };
            })
        );
    }

    private getAtgardUtskriftTitel(atgard: EntityDTO): string {
        const atgardBeteckning = atgard.name ?? `Åtgärd ${atgard.id}`;
        return this.kontraktMediatorService.kontrakt.kontraktnummer
            ? `${this.kontraktMediatorService.kontrakt.kontraktnummer}-${atgardBeteckning}`
            : atgardBeteckning;
    }
}
